const KEYS = {
  flowStart: "_bt_flow_start",
  steps: "_bt_steps",
  touches: "_bt_touches",
  inputs: "_bt_inputs",
  hasScroll: "_bt_scroll",
  hasBackspace: "_bt_backspace",
  focusChanges: "_bt_focuses",
};

const MAX_TOUCHES = 100;
const MAX_INPUTS = 200;
const MAX_FOCUSES = 50;

function load(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function save(key, val) {
  try {
    sessionStorage.setItem(key, JSON.stringify(val));
  } catch (_) { /* sessionStorage unavailable */ }
}

function append(key, item, max) {
  const list = load(key) || [];
  if (list.length < max) list.push(item);
  save(key, list);
}

export function startFlow() {
  save(KEYS.flowStart, Date.now());
  save(KEYS.steps, []);
  save(KEYS.touches, []);
  save(KEYS.inputs, []);
  save(KEYS.focusChanges, []);
  save(KEYS.hasScroll, false);
  save(KEYS.hasBackspace, false);
}

export function startStep(name) {
  const steps = load(KEYS.steps) || [];
  steps.push({ name, start: Date.now(), end: null });
  save(KEYS.steps, steps);
}

export function endStep(name) {
  const steps = load(KEYS.steps) || [];
  const step = steps.find((s) => s.name === name && !s.end);
  if (step) {
    step.end = Date.now();
    step.duration = step.end - step.start;
    save(KEYS.steps, steps);
  }
}

export function recordTouch(x, y, ts) {
  append(KEYS.touches, { x, y, t: ts }, MAX_TOUCHES);
}

export function recordInput(field, ts) {
  append(KEYS.inputs, { field, t: ts }, MAX_INPUTS);
}

export function recordScroll() {
  save(KEYS.hasScroll, true);
}

export function recordFocusChange(field, ts) {
  append(KEYS.focusChanges, { field, t: ts }, MAX_FOCUSES);
}

export function recordBackspace() {
  save(KEYS.hasBackspace, true);
}

export function computeScore() {
  const flowStart = load(KEYS.flowStart) || Date.now();
  const steps = load(KEYS.steps) || [];
  const touches = load(KEYS.touches) || [];
  const inputs = load(KEYS.inputs) || [];
  const hasScroll = load(KEYS.hasScroll) || false;
  const hasBackspace = load(KEYS.hasBackspace) || false;
  const focusChanges = load(KEYS.focusChanges) || [];

  const completedSteps = steps.filter((s) => s.end);
  const lastEnd = completedSteps.length
    ? Math.max(...completedSteps.map((s) => s.end))
    : flowStart;
  const totalDuration = lastEnd - flowStart;

  const details = [];
  let score = 0;

  // --- total time ---
  if (totalDuration < 2000) {
    return { score: 10, band: "critical", totalDuration, details: ["totalDuration < 2s"] };
  }
  if (totalDuration < 5000) {
    score++;
    details.push("totalDuration < 5s");
  }

  // --- touch analysis ---
  if (touches.length >= 3) {
    const intervals = [];
    for (let i = 1; i < touches.length; i++) {
      intervals.push(touches[i].t - touches[i - 1].t);
    }
    const avgInterval = intervals.length
      ? intervals.reduce((a, b) => a + b, 0) / intervals.length
      : 0;
    const hasPause = intervals.some((iv) => iv > 300);

    if (avgInterval < 100) {
      score++;
      details.push("touch avgInterval < 100ms");
    }
    if (!hasPause) {
      score++;
      details.push("touch noPause > 300ms");
    }

    // straight line: all Y coords within 20px range
    const ys = touches.map((t) => t.y);
    const yRange = Math.max(...ys) - Math.min(...ys);
    if (yRange <= 20) {
      score++;
      details.push("touch straightLine (yRange <= 20px)");
    }
  } else if (touches.length === 0) {
    // no touches at all — could be keyboard-only or script
    score++;
    details.push("noTouchEvents");
  }

  // --- scroll ---
  if (!hasScroll) {
    score++;
    details.push("noScroll");
  }

  // --- input analysis ---
  const uniqueFields = new Set(focusChanges.map((f) => f.field));
  const fieldCount = uniqueFields.size;

  if (fieldCount <= 1 && focusChanges.length > 0) {
    score++;
    details.push("noFieldSwitch");
  }
  if (!hasBackspace && inputs.length > 3) {
    score++;
    details.push("noCorrection (backspace)");
  }

  if (inputs.length >= 3) {
    const inputIntervals = [];
    for (let i = 1; i < inputs.length; i++) {
      inputIntervals.push(inputs[i].t - inputs[i - 1].t);
    }
    const avgInputInterval = inputIntervals.length
      ? inputIntervals.reduce((a, b) => a + b, 0) / inputIntervals.length
      : 0;
    if (avgInputInterval < 80) {
      score++;
      details.push("input avgInterval < 80ms");
    }
  }

  // --- interaction poverty: only button taps, no real input ---
  if (touches.length >= 2 && inputs.length === 0) {
    score++;
    details.push("onlyClicks");
  }

  let band;
  if (score <= 2) band = "low";
  else if (score <= 4) band = "medium";
  else if (score <= 6) band = "high";
  else band = "critical";

  return { score, band, totalDuration, details };
}

export function getPayload() {
  return {
    score: computeScore(),
    steps: load(KEYS.steps) || [],
    touchCount: (load(KEYS.touches) || []).length,
    inputCount: (load(KEYS.inputs) || []).length,
    hasScroll: load(KEYS.hasScroll) || false,
    hasBackspace: load(KEYS.hasBackspace) || false,
    focusChanges: load(KEYS.focusChanges) || [],
    flowStart: load(KEYS.flowStart),
  };
}

export function reset() {
  Object.values(KEYS).forEach((k) => {
    try { sessionStorage.removeItem(k); } catch (_) { /* sessionStorage unavailable */ }
  });
}
